import React, { useState, useEffect, useCallback } from "react";
import {
  getExtractions,
  getSourcesSingleExternal,
  getExtractionStats,
  downloadExtractionFiles,
  postStartTransform,
} from "../api/quontxApi";
import AddLabelModal from "../components/AddLabelModal";
import Pagination from "../components/Pagination";
import { truncateId } from "../utils";

const ITEMS_PER_PAGE = 10;

const Extractions = () => {
  // === STATE MANAGEMENT ===
  // State for the main data lists and maps
  const [extractions, setExtractions] = useState([]);
  const [extractionStats, setExtractionStats] = useState({});
  const [sourcesMap, setSourcesMap] = useState({});

  // State for UI control (loading, errors, modals)
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false); // Separate loading for stats
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExtraction, setSelectedExtraction] = useState(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // State for filters
  const [srcUuidFilter, setSrcUuidFilter] = useState("");
  const [etlCfgUuidFilter, setEtlCfgUuidFilter] = useState("");

  // === DATA FETCHING LOGIC ===

  // This useEffect runs ONCE on component mount to fetch all initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError("");
      try {
        const [extractionsRes, sourcesRes, statsRes] = await Promise.all([
          getExtractions(1, ITEMS_PER_PAGE),
          getSourcesSingleExternal(1, 10000),
          getExtractionStats(), // Initial stats with no filters
        ]);

        // Set state for the extractions list
        setExtractions(extractionsRes.data.data || []);
        setTotalItems(extractionsRes.data.total_count || 0);
        setCurrentPage(1);

        // Build the sources lookup map
        const sourcesArray = sourcesRes.data.data || [];
        const newSourcesMap = sourcesArray.reduce((map, source) => {
          map[source.uuid] = source.name;
          return map;
        }, {});
        setSourcesMap(newSourcesMap);

        // Set the initial stats
        setExtractionStats(statsRes.data || {});
      } catch (err) {
        setError("Failed to load initial page data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []); // The empty dependency array [] is crucial. It ensures this runs only once.

  // Dedicated function to fetch ONLY a new page of extractions
  const fetchPaginatedExtractions = useCallback(async (page) => {
    setLoading(true); // This controls the loading indicator for the table
    try {
      const response = await getExtractions(page, ITEMS_PER_PAGE);
      setExtractions(response.data.data || []);
      setTotalItems(response.data.total_count || 0);
      setCurrentPage(page);
    } catch (err) {
      setError("Failed to fetch extractions data.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Dedicated function to fetch ONLY the statistics
  const fetchFilteredStats = useCallback(async (srcUuid, etlCfgUuid) => {
    setStatsLoading(true); // Use a separate loading indicator for the stats section
    try {
      const statsRes = await getExtractionStats(srcUuid, etlCfgUuid);
      setExtractionStats(statsRes.data || {});
    } catch (err) {
      setError("Failed to fetch statistics.");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // === EVENT HANDLERS ===
  const handleOpenModal = (extraction) => {
    setSelectedExtraction(extraction);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setSelectedExtraction(null);
    setIsModalOpen(false);
  };
  const handleLabelAdded = () => {
    fetchPaginatedExtractions(currentPage);
  };
  const handlePageChange = (page) => {
    fetchPaginatedExtractions(page);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchFilteredStats(srcUuidFilter, etlCfgUuidFilter);
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // === RENDER LOGIC ===
  if (loading && extractions.length === 0) {
    return <div className="text-center p-8">Loading initial data...</div>;
  }
  if (error) return <div className="text-center p-8 text-red-400">{error}</div>;

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      {isModalOpen && selectedExtraction && (
        <AddLabelModal
          extractionId={selectedExtraction.extraction_id}
          etlConfigUuid={selectedExtraction.etl_cfg_uuid}
          initialLabel={selectedExtraction.label}
          onClose={handleCloseModal}
          onLabelAdded={handleLabelAdded}
        />
      )}

      {/* Statistics Section */}
      <h1 className="text-3xl font-bold text-white mb-6">
        Extraction Statistics
      </h1>

      <form
        onSubmit={handleFilterSubmit}
        className="bg-qtx-darker p-4 rounded-lg mb-6 flex flex-col md:flex-row items-center gap-4"
      >
        <div className="flex-1 w-full">
          <label
            htmlFor="src_uuid_filter"
            className="text-sm font-medium text-qtx-gray"
          >
            Source UUID
          </label>
          <input
            id="src_uuid_filter"
            type="text"
            value={srcUuidFilter}
            onChange={(e) => setSrcUuidFilter(e.target.value)}
            placeholder="Filter by Source UUID (optional)"
            className="w-full mt-1 bg-qtx-dark border border-qtx-gray rounded py-2 px-3 text-white leading-tight focus:outline-none focus:ring-2 focus:ring-qtx-blue"
          />
        </div>
        <div className="flex-1 w-full">
          <label
            htmlFor="etl_cfg_uuid_filter"
            className="text-sm font-medium text-qtx-gray"
          >
            ETL Config UUID
          </label>
          <input
            id="etl_cfg_uuid_filter"
            type="text"
            value={etlCfgUuidFilter}
            onChange={(e) => setEtlCfgUuidFilter(e.target.value)}
            placeholder="Filter by ETL Config UUID (optional)"
            className="w-full mt-1 bg-qtx-dark border border-qtx-gray rounded py-2 px-3 text-white leading-tight focus:outline-none focus:ring-2 focus:ring-qtx-blue"
          />
        </div>
        <button
          type="submit"
          disabled={statsLoading}
          className="bg-qtx-blue hover:bg-blue-600 text-white font-bold py-2 px-4 rounded focus:outline-none self-end h-10 mt-2 md:mt-0 disabled:bg-qtx-gray"
        >
          {statsLoading ? "Loading..." : "Apply Filters"}
        </button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <div className="bg-qtx-darker p-4 rounded-lg shadow-lg text-center">
          <p className="text-sm text-qtx-gray">Total References</p>
          <p className="text-2xl font-bold text-white">
            {extractionStats.total_references ?? "N/A"}
          </p>
        </div>
        <div className="bg-qtx-darker p-4 rounded-lg shadow-lg text-center">
          <p className="text-sm text-qtx-gray">Unique Content</p>
          <p className="text-2xl font-bold text-white">
            {extractionStats.unique_content ?? "N/A"}
          </p>
        </div>
        <div className="bg-qtx-darker p-4 rounded-lg shadow-lg text-center">
          <p className="text-sm text-qtx-gray">Deduplication Ratio</p>
          <p className="text-2xl font-bold text-white">
            {extractionStats.deduplication_ratio ?? "N/A"}
          </p>
        </div>
        <div className="bg-qtx-darker p-4 rounded-lg shadow-lg text-center">
          <p className="text-sm text-qtx-gray">Logical Size (MB)</p>
          <p className="text-2xl font-bold text-white">
            {extractionStats.total_logical_size_mb ?? "N/A"}
          </p>
        </div>
        <div className="bg-qtx-darker p-4 rounded-lg shadow-lg text-center">
          <p className="text-sm text-qtx-gray">Actual Storage (MB)</p>
          <p className="text-2xl font-bold text-white">
            {extractionStats.actual_storage_size_mb ?? "N/A"}
          </p>
        </div>
        <div className="bg-qtx-darker p-4 rounded-lg shadow-lg text-center">
          <p className="text-sm text-qtx-gray">Space Saved (MB)</p>
          <p className="text-2xl font-bold text-white">
            {extractionStats.space_saved_mb ?? "N/A"}
          </p>
        </div>
      </div>

      {/* Extractions Table Section */}
      <h1 className="text-3xl font-bold text-white mb-6">Recent Extractions</h1>
      {loading && (
        <div className="text-center p-2 text-qtx-gray">Loading list...</div>
      )}
      <div className="bg-qtx-darker shadow-lg rounded-lg overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-qtx-dark">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Extraction ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider max-w-64">
                Source Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider max-w-64">
                Etl Cfg Uuid
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Label
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                Download
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                Job Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-qtx-gray">
            {extractions.map((ext) => (
              <tr key={ext.extraction_id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                  {ext.extraction_id}
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm text-qtx-light-gray max-w-64 cursor-pointer"
                  title={sourcesMap[ext.src_uuid] || ext.src_uuid}
                  onClick={() =>
                    navigator.clipboard.writeText(
                      sourcesMap[ext.src_uuid] || ext.src_uuid
                    )
                  }
                >
                  {truncateId(sourcesMap[ext.src_uuid] || ext.src_uuid)}
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray max-w-64 cursor-pointer"
                  title={ext.etl_cfg_uuid}
                  onClick={() =>
                    navigator.clipboard.writeText(ext.etl_cfg_uuid)
                  }
                >
                  {truncateId(ext.etl_cfg_uuid)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-qtx-light-gray">
                  {ext.label && (
                    <span className="bg-qtx-dark text-qtx-light-gray px-2 py-1 rounded-md text-xs">
                      {ext.label}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                  <button
                    onClick={() => {
                      downloadExtractionFiles({
                        extraction_id: ext.extraction_id,
                      }).then((response) => {
                        const blob = new Blob([response.data]);
                        const url = window.URL.createObjectURL(blob);

                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", "extraction.zip"); // Optional: customize dynamically
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url); // cleanup
                      });
                    }}
                    className="text-qtx-blue hover:text-blue-400"
                  >
                    Download
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                  <button
                    onClick={() => handleOpenModal(ext)}
                    className="text-qtx-blue hover:text-blue-400"
                  >
                    Set Label
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                  <button
                    onClick={() =>
                      postStartTransform(
                        ext.src_uuid,
                        ext.etl_cfg_uuid,
                        ext.extraction_id,
                        ext.label
                      )
                    }
                    className="text-qtx-blue hover:text-blue-400"
                  >
                    Start Transformation
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Extractions;
